import React, { useCallback, useEffect, useState } from 'react';
import cashReservesService from '../../services/cashReservesService';

// ─── ignore-list manager (view / add / remove the shared ignore list) ──────────
const IgnoreListManager = ({ onClose, onChanged }) => {
  const [ignored, setIgnored] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await cashReservesService.getIgnoredAccounts();
      setIgnored(data.ignored || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load the ignore list');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const num = accountNumber.trim();
    if (!num) return;
    setSaving(true);
    setError('');
    try {
      await cashReservesService.addIgnoredAccount(num, { note: note.trim() });
      setAccountNumber('');
      setNote('');
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to add the account');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (num) => {
    setError('');
    try {
      await cashReservesService.removeIgnoredAccount(num);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove the account');
    }
  };

  return (
    <div
      className="cash-reserve-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="card shadow"
        style={{ maxWidth: '640px', width: '100%', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="bi bi-eye-slash me-2 text-secondary"></i>
            Manage Mgmt Fees Warnings
          </span>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
        </div>
        <div className="card-body" style={{ overflowY: 'auto' }}>
          <p className="text-muted small">
            Accounts on this list stay on the dashboard, but they are treated as waived for the
            management-fee requirement (e.g. employee accounts). Shared across reviewers; changes take
            effect on the next load.
          </p>

          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}

          <form className="row g-2 align-items-end mb-3" onSubmit={handleAdd}>
            <div className="col-sm-4">
              <label className="form-label small mb-1" htmlFor="ignoreAccountNumber">
                Account #
              </label>
              <input
                id="ignoreAccountNumber"
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g. 08001234"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </div>
            <div className="col-sm-5">
              <label className="form-label small mb-1" htmlFor="ignoreNote">
                Note (optional)
              </label>
              <input
                id="ignoreNote"
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g. Employee account"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="col-sm-3 d-grid">
              <button type="submit" className="btn btn-sm btn-primary" disabled={saving || !accountNumber.trim()}>
                <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-plus-lg'} me-1`}></i>
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>

          {isLoading ? (
            <div className="text-center py-3 text-muted">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              Loading…
            </div>
          ) : ignored.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <i className="bi bi-inbox me-2"></i>
              No accounts are being ignored.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Account #</th>
                    <th>Name / Note</th>
                    <th>Added By</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {ignored.map((row) => (
                    <tr key={row.account_number}>
                      <td className="fw-semibold">{row.account_number}</td>
                      <td>
                        {row.account_name || row.note ? (
                          <>
                            {row.account_name && <span>{row.account_name}</span>}
                            {row.note && (
                              <span className="text-muted small">
                                {row.account_name ? ' · ' : ''}
                                {row.note}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-muted small">{row.added_by || '—'}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemove(row.account_number)}
                          title="Remove from the ignore list"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IgnoreListManager;
